// page-meeting-notes.ts — Onglet « Notes » de la fiche réunion : ouverture
// de la réunion, éditeur de notes individuelles (toolbar, chargement,
// enregistrement explicite, effacement) et lancement du polling de la table
// « Nouvelles actions » (page-meeting-draft-actions).
//
// Quatrième tranche de l'extraction des closures de `renderMeetingPage`
// (audit dette technique 2026-09-07, point 16). Deux drapeaux vivaient dans
// la page et étaient lus depuis plusieurs closures :
//   - `_notesDirty` : saisie non enregistrée, lu par le switch d'onglet et
//     par le garde global de navigation ;
//   - `draftPollHandle` : intervalle du polling, arrêté à chaque switch et à
//     la navigation.
// Ils vivent maintenant ici, derrière `isDirty` / `markClean` / `stopPolling`.
// La page reste seule à décider quoi faire d'un brouillon non sauvegardé
// (confirmation) et à relancer son rendu après ouverture (`onOpened`).

import { sanitizeRichText } from '../utils/rich-text.js';
import type { DraftActionsTable } from './page-meeting-draft-actions.js';

export const DRAFT_POLL_MS = 4000;

export interface NotesTabContext {
  meetingId: number;
  /** Table des brouillons ; null en création (pas encore d'id). */
  draftTable: DraftActionsTable | null;
  /** Appelé après l'ouverture de la réunion : la page se re-rend pour que
   * les onglets et états dérivés (isOpen…) soient recalculés. */
  onOpened: () => void;
}

export interface NotesTab {
  wire(host: HTMLElement): void;
  /** Saisie non enregistrée dans l'éditeur (garde anti-perte, audit UX 2026-06). */
  isDirty(): boolean;
  /** Après confirmation d'abandon par l'utilisateur. */
  markClean(): void;
  /** À appeler à chaque switch d'onglet et à la navigation : sinon plusieurs
   * intervalles s'accumulent en arrière-plan. */
  stopPolling(): void;
}

export function createNotesTab(ctx: NotesTabContext): NotesTab {
  const { meetingId, draftTable, onOpened } = ctx;
  let dirty = false;
  let pollHandle: number | null = null;

  function stopPolling(): void {
    if (pollHandle !== null) {
      window.clearInterval(pollHandle);
      pollHandle = null;
    }
  }

  function wire(host: HTMLElement): void {
    // CTA "Ouvrir la réunion" : POST puis rappel pour que les onglets / états
    // dérivés (isOpen, isClosed, etc.) soient recalculés et que les
    // participants voient désormais l'onglet Notes.
    host.querySelector<HTMLButtonElement>('#mp-meeting-open')?.addEventListener('click', () => {
      void (async () => {
        try {
          await api.openMeetingRecord(meetingId);
          toast(t('pageMeeting.notes.toastOpened'), 'ok');
          onOpened();
        } catch (err: unknown) {
          toast(err instanceof Error ? err.message : t('common.error'), 'error');
        }
      })();
    });

    // Toolbar : preventDefault pour ne pas perdre la sélection au focus
    // sur le bouton.
    host.querySelector('#mp-notes-toolbar')?.addEventListener('mousedown', (e) => {
      e.preventDefault();
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-cmd]');
      if (btn) document.execCommand(btn.dataset['cmd'] || '', false, undefined);
    });

    const editor = host.querySelector<HTMLElement>('#mp-notes-editor');
    if (editor) {
      // Saisie = modifications non enregistrées (lu par switchTab + garde nav).
      editor.addEventListener('input', () => {
        dirty = true;
      });
      void (async () => {
        try {
          const resp = (await api.getMyMeetingNotes(meetingId)) as { content: string };
          editor.innerHTML = sanitizeRichText(resp.content);
        } catch {
          editor.innerHTML = '';
        }
        // Le chargement initial ne compte pas comme une modification.
        dirty = false;
      })();
    }

    // Feedback « notes enregistrées » affiché à côté de CHAQUE bouton
    // Enregistrer (haut + bas), pas seulement en pied de page.
    const setFeedback = (msg: string): void => {
      host.querySelectorAll<HTMLElement>('.mp-notes-feedback').forEach((el) => {
        el.textContent = msg;
      });
    };
    const saveNotes = (): void => {
      if (!editor) return;
      void (async () => {
        try {
          await api.saveMyMeetingNotes(meetingId, editor.innerHTML);
          dirty = false;
          setFeedback(t('pageMeetingsRecords.modalNotes.saved'));
          window.setTimeout(() => setFeedback(''), 2000);
        } catch (err: unknown) {
          toast(err instanceof Error ? err.message : t('common.error'), 'error');
        }
      })();
    };
    // Boutons "Enregistrer note" (haut + bas, mêmes handlers via la classe
    // .mp-notes-save-btn). Le bouton bas conserve aussi son id #mp-notes-save
    // par compat — ne pas câbler 2 fois sur l'id : la requête .querySelectorAll
    // sur la classe l'inclut déjà.
    host.querySelectorAll<HTMLButtonElement>('.mp-notes-save-btn').forEach((btn) => {
      btn.addEventListener('click', saveNotes);
    });
    host.querySelector<HTMLButtonElement>('#mp-notes-clear')?.addEventListener('click', () => {
      if (editor) {
        editor.innerHTML = '';
        dirty = true;
      }
    });

    // Zone "Nouvelles actions" : chargement initial + polling.
    if (!draftTable) return;
    void draftTable.load(false);
    pollHandle = window.setInterval(() => {
      void draftTable.load(true);
    }, DRAFT_POLL_MS);
  }

  return {
    wire,
    isDirty: () => dirty,
    markClean: () => {
      dirty = false;
    },
    stopPolling,
  };
}
